import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { SwPush } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { LogglyService } from 'ngx-loggly-logger';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AlertService } from './services/alert.service';
import { SessionWarningComponent } from './components/session-warning/session-warning.component';
import { User } from './models/user';
import { AppService } from './services/app.service';
import { AuthenticationService } from './services/authentication.service';
import { ErrorService } from './services/error.service';
import { GlobalService } from './services/global.service';
import { ModalService } from './services/modal.service';
import { OrderService } from './services/order.service';
import { SpinnerService } from './services/spinner.service';
import { UserService } from './services/user.service';
import { CustomizationService } from './services/customization.service';
import { CustomerService } from './services/customer.service';
import { CustomPageService } from './services/custom-page.service';
import { MatSidenav } from '@angular/material/sidenav';

declare const $: any;
declare function zE(a, b, c?);
declare function olark(a, b?);
declare var LiveAgent: any;

@Component({
	selector: 'app-rapidship',
	encapsulation: ViewEncapsulation.None,
	templateUrl: './app.html'
})
export class AppComponent implements OnInit, OnDestroy {
	public buildTimestamp: any;
	public currentPageTitle = '';
	public mobileView: boolean;
	public onDestroy: Subject<void>;
	public pageLoading = true;
	public sessionSnackbar: any;
	public sessionWarning: boolean;
	public sidebarCollapsed = false;
	public sidebarOpen: boolean;
	public user = new User();
	public version: any;
	public year: number;
	public widgetShown: boolean = false;
	private pageIsRefreshed: boolean = false;
	public subscriptionsUnsubscribe = new Subject<void>();
  @ViewChild('sidenav') sidenav: MatSidenav;
	constructor(private _http: HttpClient,
				private _appService: AppService,
				private _userService: UserService,
				private _router: Router,
				private _translate: TranslateService,
				private _logglyService: LogglyService,
				private _errorService: ErrorService,
				private _authenticationService: AuthenticationService,
				private _globalService: GlobalService,
				private _swPush: SwPush,
				private _cdRef: ChangeDetectorRef,
				private _modalService: ModalService,
				private _snackbar: MatSnackBar,
				private _spinnerService: SpinnerService,
				private _orderService: OrderService,
				private _alertService: AlertService,
				private _customizationService: CustomizationService,
				private _customerService: CustomerService,
				private _customPageService: CustomPageService) {
		this.onDestroy = new Subject<any>();
		// get app/build information from AppService
		this.buildTimestamp = this._appService.buildTimestamp;
		this.version = this._appService.version;
		this.year = this._appService.year;
		this._translate.addLangs(['DEFAULT', 'CUSTOM']);
		this._translate.setDefaultLang('DEFAULT');
		this._translate.use('CUSTOM');

		if (this._router.url === '/' || this._router.url.substring(0, 6) === '/login') {
			// do nothing
		} else {
			this.keepAlive();
		}

		this._router.events.subscribe((val) => {
			if (val instanceof NavigationStart) {
				// New page is loading
				this._cdRef.detectChanges();
				this.pageLoading = true;
			}

			if (val instanceof NavigationEnd) {
				// Set the page title
				const urlArray = val.urlAfterRedirects.split('/');
				const pageUrl = urlArray[1];
				const pageUrlArray = pageUrl.split('-');
				let currentPageTitle = '';
				for (let i = 0; i < pageUrlArray.length; i++) {
					if (i > 0) {
						currentPageTitle += ' ';
					}
					currentPageTitle += pageUrlArray[i].charAt(0).toUpperCase() + pageUrlArray[i].slice(1);
				}

				// Determine whether we are loading this page because it was refreshed or because it was navigated to
				this.pageIsRefreshed = val.id === 1 && val.url === val.urlAfterRedirects;

				let currentPageTitleArray = currentPageTitle.split('?');
				currentPageTitle = currentPageTitleArray[0];

				currentPageTitleArray = currentPageTitle.split(';');
				currentPageTitle = currentPageTitleArray[0];

				if (currentPageTitle === 'Reset') {
					currentPageTitle = 'Reset Password';
				}

				// Need to change Place Order to Edit Order and append order ID
				if (currentPageTitle === 'Place Order') {
					const nextOrderAction = this._orderService.getNextOrderAction();
					if (nextOrderAction?.action === 'edit') {
						currentPageTitle = `Editing Order ${nextOrderAction.id}`
					}
				}

				// Updating place order page title when using a custom override page
				if (this._customPageService.hasCustomOverride()) {
					const overridePages = this._customPageService.getStoredPages().overridePages;
					const placeOrderPage = overridePages.find(page => page.overridePage.toLowerCase().split('_').join(' ').includes(currentPageTitle.toLowerCase()));
					if (placeOrderPage) {
						currentPageTitle = placeOrderPage.name;
					}
				}

				// Check the name appended to custom-page url and set as title
				if (currentPageTitle === 'Custom Page') {
					const urlPath = window.location.hash;
  					const segments = urlPath.split('/');
 					if (segments.length > 2 && segments[1] === 'custom-page') {
 						currentPageTitle = decodeURIComponent(segments[2]);
					}
				}

				this.currentPageTitle = currentPageTitle;

				// Close any open modals
				const overlays = document.getElementsByTagName('modal-overlay');
				for (let i = 0; i < overlays.length; i++) {
					overlays[i].parentElement.removeChild(overlays[i]);
				}
				// Remove modal-open classes
				const modalOpen = document.getElementsByClassName('modal-open');
				while (modalOpen.length) {
					modalOpen[0].className = modalOpen[0].className.replace('modal-open', '');
				}

				// Close the sidebar when navigating for mobile view
				if (this.sidebarOpen && this.mobileView) {
					this.sidebarOpen = false;
				}

				this.checkChatWidget();

				// New page is done loading
				this._cdRef.detectChanges();
				this.pageLoading = false;
			}
		});

		this.checkWidth = this.checkWidth.bind(this);
		this.toggleSidebarCollapsed = this.toggleSidebarCollapsed.bind(this);
		this.toggleSidebarOpen = this.toggleSidebarOpen.bind(this);
		this.checkChatWidget = this.checkChatWidget.bind(this);
	}

	checkChatWidget(userIsLoggedOut : boolean = false, firstLoad : boolean = false) {
		const widgetConfig = this._customizationService.retrieveWidgetConfig();

		if (widgetConfig && widgetConfig?.loadScript) {
			let widgetShouldBeShown: boolean = false;

			// If page is still loading during a refresh, then we are not ready to check the widget yet.
			// During refresh, URL seems to be set to '/' for a split second before changing to the actual URL.
			if (this.pageLoading && this._router.url === '/'){
				return;
			}

			// Check if the page we are on should show the widget
			if (userIsLoggedOut || this._router.url === '/' || this._router.url === '/home' || this._router.url.substring(0, 6) === '/login') {
				if (widgetConfig.pageConfig?.activeOnLogin) {
					widgetShouldBeShown = true;
				}
			} else if (widgetConfig.pageConfig?.activeOnPlaceOrder && (this._router.url === '/place-order')) {
				widgetShouldBeShown = true;
			} else if (widgetConfig.pageConfig?.activeOnTrackOrder && (this._router.url === '/track-order')) {
				widgetShouldBeShown = true;
			} else if (widgetConfig.pageConfig?.activeOnReports && (this._router.url === '/reports')) {
				widgetShouldBeShown = true;
			} else if (widgetConfig.pageConfig?.activeOnRouteTracking && (this._router.url === '/route-tracking')) {
				widgetShouldBeShown = true;
			} else if (widgetConfig.pageConfig?.activeOnParcelTracking && (this._router.url === '/track-parcel')) {
				widgetShouldBeShown = true;
			} else if (widgetConfig.pageConfig?.activeOnAddressBook && (this._router.url === '/address-book')) {
				widgetShouldBeShown = true;
			} else if (widgetConfig.pageConfig?.activeOnOrderTemplates && (this._router.url === '/order-templates')) {
				widgetShouldBeShown = true;
			} else if (widgetConfig.pageConfig?.activeOnInvoices && (this._router.url === '/invoices')) {
				widgetShouldBeShown = true;
			} else if (widgetConfig.pageConfig?.activeOnUserProfile && (this._router.url === '/user-profile')) {
				widgetShouldBeShown = true;
			}

			// If widget should be displayed, confirm it is either available to everyone or
			// the selected customer is allowed to see it
			if (!widgetConfig.allowForAllCustomers) {
				const selectedCustomer = this._customerService.getSelectedCustomer();
				if (widgetShouldBeShown && widgetConfig.allowedCustIds) {
					if (selectedCustomer) {
						const match = widgetConfig.allowedCustIds.find(x => x === selectedCustomer.id);
						if (!match) {
							widgetShouldBeShown = false;
						}
					}
				}
			}

			// If a widget is being displayed, move the help button over a little to make space for the widget
			setTimeout(() => {
				let helpFabButton = document.getElementById('PAGE_HELP');
				if (helpFabButton) {
					helpFabButton.className = widgetShouldBeShown ? 'fab-container-hidden' : 'fab-container';
				}
			}, 0);
			this.widgetShown = widgetShouldBeShown;
			this._customizationService.widgetIsShown$.next(widgetShouldBeShown);

			const isZendeskWidget : boolean = widgetConfig.loadScript.includes('zdassets.com');
			const isOlarkWidget : boolean = widgetConfig.loadScript.includes('olark.identify');
			const isLiveAgentWidget : boolean = widgetConfig.loadScript.includes('LiveAgent.createButton');

			// Widget loads in an iframe. Set the visibility based on above calculations
			if (isZendeskWidget && typeof zE === 'function') {
				zE('webWidget', widgetShouldBeShown ? 'show' : 'hide');
			} else if (isOlarkWidget && typeof olark === 'function') {
				if (widgetShouldBeShown) {
					olark('api.box.show');
				} else {
					olark('api.box.hide');
				}
			} else if (isLiveAgentWidget) {
				for (let [key, value] of LiveAgent.instance.elements.entries()) {
					setTimeout(() => {
						let liveAgentWidget = document.getElementById(key);
						if (liveAgentWidget) {
							liveAgentWidget.style.visibility = widgetShouldBeShown ? 'visible' : 'hidden';
						}
					}, firstLoad || this.pageIsRefreshed ? 1000 : 0);
				}
			} else {
				if (firstLoad || this.pageIsRefreshed) {
					this.waitForElement('iframe', 10000).then(function(){
						// even though an iframe is loaded, it may not be the one we are looking for
						// some chat widgets use multiple iframes, so we need to loop through the show/hide logic multiple times
						// until we exhaust all of the iframes the chat widget uses. Check for additional iframes for 2 more seconds
						// with 100ms delay between each check. This should ensure we don't have a "flicker" effect with the widget
						for (let i = 1; i <= 20; i++) {
							setTimeout(() => {
								let nodes = document.querySelectorAll('iframe');
								nodes.forEach(iframe => {
									if (iframe.id) {
										iframe.style.visibility = widgetShouldBeShown ? 'visible' : 'hidden';
									}
								});
							}, i * 100);
						}
					}).catch(()=>{
						// if we don't find an iframe after 10 seconds, then we just give up
					});
				} else {
					setTimeout(() => {
						let nodes = document.querySelectorAll('iframe');
						nodes.forEach(iframe => {
							if (iframe.id) {
								iframe.style.visibility = widgetShouldBeShown ? 'visible' : 'hidden';
							}
						});
					}, 0);
				}
			}
		}
	}

	waitForElement(querySelector, timeout){
		return new Promise<void>((resolve, reject) => {
			let timer;
			if(document.querySelectorAll(querySelector).length) return resolve();

			const observer = new MutationObserver(() => {
				if(document.querySelectorAll(querySelector).length){
					observer.disconnect();
					if(timer !== false) clearTimeout(timer);
					return resolve();
				}
			});

			observer.observe(document.body, {
				childList: true,
				subtree: true
			});

			if(timeout) timer = setTimeout(()=>{
				observer.disconnect();
				reject();
			}, timeout);
		});
	}

	checkWidth() {
		const windowSize = $(window).width();
		if (windowSize < 993) {
			if (this.sidebarCollapsed) {
				this.sidebarCollapsed = false;
			}
			this.sidebarOpen = false;
			this.mobileView = true;
		} else {
			this.sidebarOpen = true;
			this.mobileView = false;
		}
	}

	keepAlive() {
		this._spinnerService.startSpinner();
		this._authenticationService.renewSession(this.user).subscribe({
			next: () => {
				this.sessionWarning = false;
				this._spinnerService.stopSpinner();
			}, error: (error) => {
				this._spinnerService.stopSpinner();
				this._errorService.error(error, 'renewSession failed');
				this._authenticationService.idleState$.emit('Unable to renew session');
				this._authenticationService.authenticationDelete({
					detailedMsg: 'Please enter your login information again to continue using Client Portal.',
					generalMsg: 'You have been logged out.'
				});
				this.checkChatWidget(true);
				this._alertService.clear();
			}
		});
	}

	ngOnDestroy() {
		this.onDestroy.next();
		this.subscriptionsUnsubscribe.next();
	}

	ngOnInit() {
		fromEvent(window, 'resize').pipe(takeUntil(this.onDestroy)).subscribe(() => this.checkWidth());
		this._userService.user$.subscribe((data) => {
			this.user = data;
			if (this.user && this.user.token) {
				this._authenticationService.startSessionTimer();
			}
		});

		this._customizationService.widgetScriptLoaded$.subscribe(() => {
			const selectedCustomer = this._customerService.getSelectedCustomer();
			setTimeout(() => {
				this.checkChatWidget(false, true);
				this.applyChatWidgetCustomerCustomization(selectedCustomer);
			}, 250);
		});

		this._authenticationService.showAuthModal$.pipe(debounceTime(500)).subscribe((error) => {
			this._modalService.displayLoginModal(error);
			this.checkChatWidget(true);
		});
			this._authenticationService.showTimeoutBanner$.pipe(takeUntil(this.subscriptionsUnsubscribe)).subscribe((value) => {

				if (value && !this.sessionWarning) {
				this.sessionWarning = value;
				const config = new MatSnackBarConfig();
				config.verticalPosition = 'top';
				config.horizontalPosition = 'center';
				config.politeness = 'assertive';
				config.panelClass = 'session-warning';
				this.sessionSnackbar = this._snackbar.openFromComponent(SessionWarningComponent, config);
				this.sessionSnackbar.afterDismissed().subscribe(() => {
					this.keepAlive();
				});
				// Made changes to timeout banner XD-27494
			} else if (!value && this.sessionWarning) {
				this.sessionWarning = value;
				this.sessionSnackbar?.dismiss();
			}
		});
		this._logglyService.push({
			logglyKey: 'ab97aee8-1181-4136-a49b-08364df319f3',
			sendConsoleErrors: true,
			tag: 'Rapidship'
		});

		this._authenticationService.loggedIn$.subscribe((data) => {
			if (data === 'inet') {
				this._alertService.clear();
				this._globalService.getBroadcastMessage().subscribe({
					next: (response) => {
						if (response.length > 0) {
							response.forEach(element => {
								this._alertService.alert(element.comment, element.messageLevel, {keepAfterRouteChange: true, autoClose: false, hasCloseButton: !element.alwaysDisplay});
							});
						}
					}, error: (error) => {
						this._errorService.error(error, 'getBroadcastMessage failed', 'modal');
					}
				});
			}
		});

		// BroadcastMessages will be called again in event of refreshing the page
		if (this.user.type == 'inet') {
			this._globalService.getBroadcastMessage().subscribe({
				next: (response) => {
					if (response.length > 0) {
						response.forEach(element => {
							this._alertService.alert(element.comment, element.messageLevel, {keepAfterRouteChange: true, autoClose: false, hasCloseButton: !element.alwaysDisplay});
						});
					}
				}, error: (error) => {
					this._errorService.error(error, 'getBroadcastMessage failed', 'modal');
				}
			});
		}

		this._appService.checkCustomLogo();

		const sidebarCollapsed = localStorage.getItem('Rapidship:sidebarCollapsed');
		if (sidebarCollapsed) {
			this.sidebarCollapsed = JSON.parse(sidebarCollapsed);
		}

		this.checkWidth();
		setTimeout(() => this.checkChatWidget(), 250);

		this._swPush.notificationClicks.subscribe((event) => {
			// TODO: We may want to incorporate the use of actions and only take action on those action
			// But for now this navigates to track order with the tracking id and is only fired if the app is open
			if (event.notification.data && event.notification.data.trackingId) {
				this._router.navigate(['/track-order', {trackingId: event.notification.data.trackingId}]);
			}
		});

		// Every time selected customer is changed, update widget details
		this._customerService.selectedCustomer$.subscribe(selectedCustomer => {
			if (selectedCustomer) {
				this.applyChatWidgetCustomerCustomization(selectedCustomer);
			}
		});
	}

	toggleSidebarCollapsed() {
		this.sidebarCollapsed = !this.sidebarCollapsed;
		localStorage.setItem('Rapidship:sidebarCollapsed', JSON.stringify(this.sidebarCollapsed));
	}

	toggleSidebarOpen() {
		this.sidebarOpen = !this.sidebarOpen;
	}

	applyChatWidgetCustomerCustomization(selectedCustomer) {
		const widgetConfig = this._customizationService.retrieveWidgetConfig();
		if (!selectedCustomer) {
			selectedCustomer = {
				customerName: '',
				defaultEmailAddress: ''
			}
		}

		// Confirm a chat widget exists and if so, run customization script.
		if (widgetConfig && widgetConfig?.loadScript && widgetConfig?.additionalScript) {
			let userFirstName = this.user.friendlyName?.split(' ', 1)[0] ?? '';
			let userLastName = this.user.friendlyName?.replace(userFirstName, '');
			let primaryCustomerFirstName = this.user.customerName?.split(' ', 1)[0] ?? '';
			let primaryCustomerLastName = this.user.customerName?.replace(primaryCustomerFirstName, '');
			let selectedCustomerFirstName = selectedCustomer.customerName?.split(' ', 1)[0] ?? '';
			let selectedCustomerLastName = selectedCustomer.customerName?.replace(selectedCustomerFirstName, '');

			let script = 'let username = \'' + this.user.username + '\';\n';
			script += 'let userFullName = \'' + this.user.friendlyName + '\';\n';
			script += 'let userFirstName = \'' + userFirstName + '\';\n';
			script += 'let userLastName = \'' + userLastName + '\';\n';
			script += 'let userEmail = \'' + this.user.inetUser?.email + '\';\n';
			script += 'let primaryCustomerFullName = \'' + this.user.customerName + '\';\n';
			script += 'let primaryCustomerFirstName = \'' + primaryCustomerFirstName + '\';\n';
			script += 'let primaryCustomerLastName = \'' + primaryCustomerLastName + '\';\n';
			script += 'let selectedCustomerFullName = \'' + selectedCustomer.customerName + '\';\n';
			script += 'let selectedCustomerFirstName = \'' + selectedCustomerFirstName + '\';\n';
			script += 'let selectedCustomerLastName = \'' + selectedCustomerLastName + '\';\n';
			script += 'let selectedCustomerEmail = \'' + selectedCustomer.defaultEmailAddress + '\';\n';
			script += widgetConfig.additionalScript;

			setTimeout(() => (window as any).eval(script), 100);
		}
	}

  close(reason: string) {
		if(reason == 'escape'){
			this.sidenav.open();
		}
	}
}
